'use client'

import React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { useBirhaus } from '@birhaus/provider'
import { useTheme } from '@birhaus/themes'
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  Eye,
  FileText,
  TrendingUp,
  TrendingDown,
  Clock,
  Users,
  DollarSign,
  Flag,
  Sparkles,
  Download,
  RefreshCw
} from 'lucide-react'

// Local cn utility  
const cn = (...inputs: (string | boolean | undefined | null)[]) => twMerge(clsx(inputs))

// V3 SEPRELAD Dashboard variants with glass morphism
const seprelaDashboardV3Variants = cva(
  'group relative transition-all duration-700 ease-smooth overflow-hidden backdrop-blur-md border border-white/30',
  {
    variants: {
      glassVariant: {
        subtle: 'bg-white/40 shadow-lg hover:shadow-xl',
        medium: 'bg-white/60 shadow-xl hover:shadow-2xl backdrop-saturate-150',
        intense: 'bg-white/80 shadow-2xl hover:shadow-3xl backdrop-saturate-200'
      },
      borderRadius: {
        moderate: 'rounded-2xl',
        generous: 'rounded-3xl',
        radical: 'rounded-[2.5rem]'
      },
      spacing: {
        comfortable: 'p-6',
        generous: 'p-8',
        radical: 'p-12'
      },
      complianceStatus: {
        compliant: 'border-green-300/50 bg-gradient-to-br from-green-50/30 to-white/50',
        warning: 'border-amber-300/50 bg-gradient-to-br from-amber-50/30 to-white/50',
        critical: 'border-red-300/50 bg-gradient-to-br from-red-50/30 to-white/50'
      },
      floating: {
        true: 'hover:scale-[1.005] hover:-translate-y-0.5',
        false: ''
      }
    },
    defaultVariants: {
      glassVariant: 'medium',
      borderRadius: 'generous',
      spacing: 'generous',
      complianceStatus: 'compliant',
      floating: false
    }
  }
)

const metricCardVariants = cva(
  'transition-all duration-300 backdrop-blur-sm border border-white/40 rounded-2xl hover:shadow-lg',
  {
    variants: {
      status: {
        success: 'bg-green-100/60 border-green-200/50',
        warning: 'bg-amber-100/60 border-amber-200/50',
        danger: 'bg-red-100/60 border-red-200/50',
        neutral: 'bg-white/70 border-white/50'
      }
    },
    defaultVariants: {
      status: 'neutral'
    }
  }
)

export interface SEPRELADReportV3 {
  id: string
  type: 'ROS' | 'ROI' | 'RTE' | 'RPF' // Paraguay SEPRELAD report types
  date: Date
  amount: number
  currency: 'PYG' | 'USD'
  status: 'pending' | 'submitted' | 'approved' | 'rejected'
  entityName: string
  entityId: string
  transactionCount: number
  riskLevel: 'low' | 'medium' | 'high'
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  submittedBy?: string
  reviewedBy?: string
  dueDate?: Date
}

export interface SEPRELADMetricsV3 {
  totalReports: number
  pendingReports: number
  overdueReports: number
  complianceRate: number
  avgProcessingTime: number // days
  monthlyTrend: {
    reports: number
    change: number
  }
  riskDistribution: {
    low: number
    medium: number
    high: number
  }
  reportTypes: {
    ROS: number
    ROI: number  
    RTE: number
    RPF: number
  }
}

export interface BirhausSEPRELADDashboardV3Props extends VariantProps<typeof seprelaDashboardV3Variants> {
  // SEPRELAD data
  reports: SEPRELADReportV3[]
  metrics: SEPRELADMetricsV3
  
  // V3 Glass styling
  enableGlass?: boolean
  glassIntensity?: 'subtle' | 'medium' | 'intense'
  enableFloating?: boolean
  reducedMotion?: boolean
  
  // Display options
  showMetrics?: boolean
  showRecentReports?: boolean
  showComplianceStatus?: boolean
  showRiskMatrix?: boolean
  maxRecentReports?: number
  
  // Permissions
  canViewReports?: boolean
  canSubmitReports?: boolean
  canApproveReports?: boolean
  canDownloadReports?: boolean
  
  // Theme
  themeName?: 'financial' | 'compliance' | 'educational' | 'default'
  
  // Interaction
  onReportClick?: (report: SEPRELADReportV3) => void
  onCreateReport?: (type: string) => void
  onDownloadReport?: (reportId: string) => void
  onRefreshData?: () => void
  
  // Standard props
  className?: string
  title?: string
  titleEs?: string
  titleEn?: string
}

/**
 * BirhausSEPRELADDashboardV3 - Glass morphism dashboard for Paraguay SEPRELAD compliance
 * 
 * Features:
 * - Glass morphism compliance status indicators
 * - SEPRELAD-specific report types (ROS, ROI, RTE, RPF)
 * - Real-time compliance metrics with glass styling
 * - Risk assessment matrix with floating effects
 * - Spanish-first regulatory terminology
 * - Overdue report highlighting with amber glass tints
 * - Generous spacing for regulatory data clarity
 * - GPU-accelerated status transitions
 */
export function BirhausSEPRELADDashboardV3({
  reports,
  metrics,
  enableGlass = true,
  glassVariant = 'medium',
  borderRadius = 'generous',
  spacing = 'generous',
  complianceStatus,
  floating = false,
  showMetrics = true,
  showRecentReports = true,
  showComplianceStatus = true,
  showRiskMatrix = true,
  maxRecentReports = 5,
  canViewReports = true,
  canSubmitReports = false,
  canApproveReports = false,
  canDownloadReports = false,
  reducedMotion = false,
  themeName = 'financial',
  onReportClick,
  onCreateReport,
  onDownloadReport,
  onRefreshData,
  className,
  title,
  titleEs,
  titleEn,
  ...props
}: BirhausSEPRELADDashboardV3Props) {
  const { language } = useBirhaus()
  const theme = useTheme(themeName)
  const [isRefreshing, setIsRefreshing] = React.useState(false)
  const [selectedTimeRange, setSelectedTimeRange] = React.useState<'7d' | '30d' | '90d' | '1y'>('30d')

  // Calculate overall compliance status
  const overallComplianceStatus = React.useMemo(() => {
    const complianceRate = metrics.complianceRate
    const overdueCount = metrics.overdueReports
    
    if (overdueCount > 0 || complianceRate < 80) return 'critical'
    if (complianceRate < 95) return 'warning'
    return 'compliant'
  }, [metrics])

  // Format currency for SEPRELAD reports
  const formatCurrency = React.useCallback((value: number, currency: string): string => {
    const locale = language === 'es' ? 'es-PY' : 'en-US'
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency === 'PYG' ? 'PYG' : 'USD',
      minimumFractionDigits: currency === 'PYG' ? 0 : 2,
      maximumFractionDigits: currency === 'PYG' ? 0 : 2
    }).format(value)
  }, [language])

  // Get report type info
  const getReportTypeInfo = React.useCallback((type: string) => {
    const types = {
      ROS: {
        name: language === 'es' ? 'Reporte de Operación Sospechosa' : 'Suspicious Operation Report',
        icon: AlertTriangle,
        color: 'text-red-600'
      },
      ROI: {
        name: language === 'es' ? 'Reporte de Operación Inusual' : 'Unusual Operation Report',
        icon: Eye,
        color: 'text-amber-600'
      },
      RTE: {
        name: language === 'es' ? 'Reporte de Transferencia Electrónica' : 'Electronic Transfer Report',
        icon: TrendingUp,
        color: 'text-blue-600'
      },
      RPF: {
        name: language === 'es' ? 'Reporte de Persona Física/Jurídica' : 'Individual/Entity Report',
        icon: Users,
        color: 'text-purple-600'
      }
    }
    
    return types[type as keyof typeof types] || {
      name: type,
      icon: FileText,
      color: 'text-gray-600'
    }
  }, [language])

  // Get status info
  const getStatusInfo = React.useCallback((status: string) => {
    const statuses = {
      pending: {
        icon: Clock,
        color: 'text-amber-600',
        label: language === 'es' ? 'Pendiente' : 'Pending',
        bgColor: 'bg-amber-100/60'
      },
      submitted: {
        icon: CheckCircle,
        color: 'text-blue-600',
        label: language === 'es' ? 'Enviado' : 'Submitted',
        bgColor: 'bg-blue-100/60'
      },
      approved: {
        icon: CheckCircle,
        color: 'text-green-600',
        label: language === 'es' ? 'Aprobado' : 'Approved',
        bgColor: 'bg-green-100/60'
      },
      rejected: {
        icon: AlertTriangle,
        color: 'text-red-600',
        label: language === 'es' ? 'Rechazado' : 'Rejected',
        bgColor: 'bg-red-100/60'
      }
    }
    
    return statuses[status as keyof typeof statuses] || statuses.pending
  }, [language])

  // Handle refresh
  const handleRefresh = React.useCallback(async () => {
    setIsRefreshing(true)
    try {
      await onRefreshData?.()
    } finally {
      setIsRefreshing(false)
    }
  }, [onRefreshData])

  // Recent reports sorted by date
  const recentReports = React.useMemo(() => {
    return reports
      .sort((a, b) => b.date.getTime() - a.date.getTime())
      .slice(0, maxRecentReports)
  }, [reports, maxRecentReports])

  return (
    <div
      className={cn(
        seprelaDashboardV3Variants({
          glassVariant: enableGlass ? glassVariant : undefined,
          borderRadius,
          spacing,
          complianceStatus: complianceStatus || overallComplianceStatus,
          floating: floating && !reducedMotion
        }),
        'space-y-6',
        !enableGlass && 'bg-white border-gray-200',
        className
      )}
      {...props}
    >
      {/* Glass gradient overlay */}
      {enableGlass && (
        <div className="absolute inset-0 rounded-[inherit] bg-gradient-to-br from-white/20 via-transparent to-white/5 pointer-events-none" />
      )}

      {/* Floating sparkle effects */}
      {enableGlass && floating && (
        <div className="absolute top-6 right-6 text-blue-400/40">
          <Sparkles size={20} className="animate-pulse" />
        </div>
      )}

      {/* Header */}
      <div className="relative flex items-center justify-between">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <Shield size={28} className="text-green-600" />
            {language === 'es' 
              ? (titleEs || title || 'Dashboard SEPRELAD')
              : (titleEn || title || 'SEPRELAD Dashboard')
            }
          </h1>
          <p className="text-gray-600">
            {language === 'es' 
              ? 'Monitoreo de cumplimiento regulatorio - Paraguay'
              : 'Regulatory compliance monitoring - Paraguay'
            }
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Refresh button */}
          {onRefreshData && (
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={cn(
                'p-3 rounded-2xl transition-all duration-300',
                enableGlass 
                  ? 'bg-white/60 hover:bg-white/80 backdrop-blur-sm border border-white/40'
                  : 'bg-gray-100 hover:bg-gray-200',
                isRefreshing && 'animate-spin'
              )}
            >
              <RefreshCw size={20} className="text-gray-600" />
            </button>
          )}

          {/* Overall compliance status */}
          {showComplianceStatus && (
            <div className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-xl',
              enableGlass 
                ? 'backdrop-blur-sm border border-white/40'
                : 'border',
              overallComplianceStatus === 'compliant' && 'bg-green-100/60 text-green-700 border-green-200/50',
              overallComplianceStatus === 'warning' && 'bg-amber-100/60 text-amber-700 border-amber-200/50',
              overallComplianceStatus === 'critical' && 'bg-red-100/60 text-red-700 border-red-200/50'
            )}>
              {overallComplianceStatus === 'compliant' && <CheckCircle size={16} />}
              {overallComplianceStatus === 'warning' && <AlertTriangle size={16} />}
              {overallComplianceStatus === 'critical' && <AlertTriangle size={16} />}
              <span className="text-sm font-medium">
                {language === 'es' 
                  ? overallComplianceStatus === 'compliant' ? 'Conforme' :
                    overallComplianceStatus === 'warning' ? 'Atención' : 'Crítico'
                  : overallComplianceStatus === 'compliant' ? 'Compliant' :
                    overallComplianceStatus === 'warning' ? 'Warning' : 'Critical'
                }
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Metrics cards */}
      {showMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Reports */}
          <div className={cn(
            metricCardVariants({ status: 'neutral' }),
            'p-4'
          )}>
            <div className="flex items-center gap-3">
              <div className={cn(
                'p-3 rounded-xl',
                enableGlass 
                  ? 'bg-white/60 backdrop-blur-sm border border-white/40'
                  : 'bg-blue-100'
              )}>
                <FileText size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">
                  {language === 'es' ? 'Total Reportes' : 'Total Reports'}
                </p>
                <p className="text-xl font-bold text-gray-800">
                  {metrics.totalReports.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* Pending Reports */}
          <div className={cn(
            metricCardVariants({ 
              status: metrics.pendingReports > 0 ? 'warning' : 'success' 
            }),
            'p-4'
          )}>
            <div className="flex items-center gap-3">
              <div className={cn(
                'p-3 rounded-xl',
                enableGlass 
                  ? 'bg-white/60 backdrop-blur-sm border border-white/40'
                  : 'bg-amber-100'
              )}>
                <Clock size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">
                  {language === 'es' ? 'Pendientes' : 'Pending'}
                </p>
                <p className="text-xl font-bold text-gray-800">
                  {metrics.pendingReports}
                </p>
              </div>
            </div>
          </div>

          {/* Overdue Reports */}
          <div className={cn(
            metricCardVariants({ 
              status: metrics.overdueReports > 0 ? 'danger' : 'success' 
            }),
            'p-4'
          )}>
            <div className="flex items-center gap-3">
              <div className={cn(
                'p-3 rounded-xl',
                enableGlass 
                  ? 'bg-white/60 backdrop-blur-sm border border-white/40'
                  : 'bg-red-100'
              )}>
                <AlertTriangle size={20} className="text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">
                  {language === 'es' ? 'Vencidos' : 'Overdue'}
                </p>
                <p className="text-xl font-bold text-gray-800">
                  {metrics.overdueReports}
                </p>
              </div>
            </div>
          </div>

          {/* Compliance Rate */}
          <div className={cn(
            metricCardVariants({ 
              status: metrics.complianceRate >= 95 ? 'success' : 
                     metrics.complianceRate >= 80 ? 'warning' : 'danger'
            }),
            'p-4'
          )}>
            <div className="flex items-center gap-3">
              <div className={cn(
                'p-3 rounded-xl',
                enableGlass 
                  ? 'bg-white/60 backdrop-blur-sm border border-white/40'
                  : 'bg-green-100'
              )}>
                <TrendingUp size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">
                  {language === 'es' ? 'Cumplimiento' : 'Compliance'}
                </p>
                <p className="text-xl font-bold text-gray-800">
                  {metrics.complianceRate.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Types Distribution */}
      {showRiskMatrix && (
        <div className={cn(
          'p-6 rounded-2xl transition-colors duration-300',
          enableGlass 
            ? 'bg-white/40 backdrop-blur-sm border border-white/30'
            : 'bg-gray-50'
        )}>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {language === 'es' ? 'Distribución por Tipo' : 'Distribution by Type'}
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(metrics.reportTypes).map(([type, count]) => {
              const typeInfo = getReportTypeInfo(type)
              return (
                <div
                  key={type}
                  className={cn(
                    'p-4 rounded-xl transition-all duration-300',
                    enableGlass 
                      ? 'bg-white/50 hover:bg-white/70 backdrop-blur-sm border border-white/40'
                      : 'bg-white hover:shadow-sm'
                  )}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <typeInfo.icon size={16} className={typeInfo.color} />
                    <span className="text-sm font-medium text-gray-800">
                      {type}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-gray-800">{count}</div>
                  <div className="text-xs text-gray-600 truncate">
                    {typeInfo.name}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Recent Reports */}
      {showRecentReports && recentReports.length > 0 && (
        <div className={cn(
          'p-6 rounded-2xl transition-colors duration-300',
          enableGlass 
            ? 'bg-white/40 backdrop-blur-sm border border-white/30'
            : 'bg-gray-50'
        )}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">
              {language === 'es' ? 'Reportes Recientes' : 'Recent Reports'}
            </h3>
            {canDownloadReports && (
              <button
                onClick={() => onDownloadReport?.('')}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
                  enableGlass 
                    ? 'bg-white/60 hover:bg-white/80 backdrop-blur-sm border border-white/40'
                    : 'bg-white hover:bg-gray-50'
                )}
              >
                <Download size={16} />
                {language === 'es' ? 'Exportar' : 'Export'}
              </button>
            )}
          </div>

          <div className="space-y-3">
            {recentReports.map((report) => {
              const typeInfo = getReportTypeInfo(report.type)
              const statusInfo = getStatusInfo(report.status)
              
              return (
                <div
                  key={report.id}
                  onClick={() => canViewReports && onReportClick?.(report)}
                  className={cn(
                    'p-4 rounded-xl border transition-all duration-300',
                    enableGlass 
                      ? 'bg-white/50 hover:bg-white/70 border-white/40 backdrop-blur-sm'
                      : 'bg-white hover:shadow-sm border-gray-200',
                    canViewReports && 'cursor-pointer'
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        'p-2 rounded-lg',
                        enableGlass 
                          ? 'bg-white/60 backdrop-blur-sm border border-white/40'
                          : 'bg-gray-100'
                      )}>
                        <typeInfo.icon size={16} className={typeInfo.color} />
                      </div>
                      <div>
                        <div className="font-medium text-gray-800">
                          {report.type} - {report.entityName}
                        </div>
                        <div className="text-sm text-gray-600">
                          {report.date.toLocaleDateString(
                            language === 'es' ? 'es-PY' : 'en-US'
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="font-medium text-gray-800">
                        {formatCurrency(report.amount, report.currency)}
                      </div>
                      <div className={cn(
                        'inline-flex items-center gap-1 px-2 py-1 rounded text-xs',
                        enableGlass 
                          ? statusInfo.bgColor + ' backdrop-blur-sm'
                          : statusInfo.bgColor
                      )}>
                        <statusInfo.icon size={12} className={statusInfo.color} />
                        {statusInfo.label}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Actions */}
      {canSubmitReports && (
        <div className="flex items-center gap-3">
          <button
            onClick={() => onCreateReport?.('ROS')}
            className={cn(
              'px-6 py-3 rounded-2xl font-medium text-white transition-all duration-300',
              'bg-red-600 hover:bg-red-700 active:bg-red-800',
              enableGlass && 'backdrop-blur-sm shadow-lg hover:shadow-xl'
            )}
          >
            {language === 'es' ? 'Crear ROS' : 'Create ROS'}
          </button>
          <button
            onClick={() => onCreateReport?.('ROI')}
            className={cn(
              'px-6 py-3 rounded-2xl font-medium transition-all duration-300',
              enableGlass 
                ? 'bg-white/60 hover:bg-white/80 text-gray-700 backdrop-blur-sm border border-white/40'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            )}
          >
            {language === 'es' ? 'Crear ROI' : 'Create ROI'}
          </button>
        </div>
      )}
    </div>
  )
}

export type { BirhausSEPRELADDashboardV3Props, SEPRELADReportV3, SEPRELADMetricsV3 }